import { FormattedMessage, useIntl } from 'react-intl';
import { useGetCurrentOrganization } from '@/api/trafikportalen/trafikportalenAPI';
import { cn } from '@/helpers/classnames';
import { Skeleton } from './Skeleton';
import { useOrgCreditInfo } from '@/hooks/useOrgCreditInfo';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useHasSchoolDepartmentAccess } from '@/hooks/useHasSchoolDepartmentAccess';
import { CreditInfoBar } from './CreditInfoBar';
import { useMemo } from 'react';

export const OrganizationCreditInfo = ({
  includeTitle = true,
  className,
}: {
  includeTitle?: boolean;
  className?: string;
}) => {
  const { data: organization } = useGetCurrentOrganization();
  const {
    allpayCredit,
    cardCredit,
    portalSpent,
    totalSpent,
    remainingCredit,
    isLoading,
    hasNoCreditLimit,
  } = useOrgCreditInfo();
  const { formatMessage, locale } = useIntl();
  const hasSchoolDepartmentAccess = useHasSchoolDepartmentAccess();
  const studentsDisabled = useStudentsDisabled();

  const {
    allpayPercentage,
    cardPercentage,
    portalPercentage,
    remainingPercentage,
  } = useMemo(() => {
    if (!organization || isLoading) {
      return {
        allpayPercentage: 0,
        cardPercentage: 0,
        portalPercentage: 0,
        remainingPercentage: 0,
      };
    }

    if (organization.credit === 0) {
      return {
        allpayPercentage: 0,
        cardPercentage: 0,
        portalPercentage: 0,
        remainingPercentage: 0,
      };
    }

    if (totalSpent === 0) {
      return {
        allpayPercentage: 0,
        cardPercentage: 0,
        portalPercentage: 0,
        remainingPercentage: 0,
      };
    }

    if (organization.credit === undefined) {
      return {
        allpayPercentage: (allpayCredit / totalSpent) * 100,
        cardPercentage: (cardCredit / totalSpent) * 100,
        portalPercentage: (portalSpent / totalSpent) * 100,
        remainingPercentage: (remainingCredit / totalSpent) * 100,
      };
    }

    return {
      allpayPercentage: (allpayCredit / organization.credit) * 100,
      cardPercentage: (cardCredit / organization.credit) * 100,
      portalPercentage: (portalSpent / organization.credit) * 100,
      remainingPercentage: (remainingCredit / organization.credit) * 100,
    };
  }, [
    organization,
    isLoading,
    allpayCredit,
    cardCredit,
    portalSpent,
    remainingCredit,
    totalSpent,
  ]);

  if (!organization || isLoading) return <Skeleton />;

  const barItems = [
    {
      label: formatMessage({ defaultMessage: 'Tjänsteresor i app' }),
      bgColor: 'bg-allpay',
      color: 'text-allpayContrast',
      value: allpayCredit,
      percentage: allpayPercentage,
    },
    {
      label: formatMessage({ defaultMessage: 'Tjänsteresor på kort' }),
      bgColor: 'bg-smartcard',
      color: 'text-smartcardContrast',
      value: cardCredit,
      percentage: cardPercentage,
    },
    {
      label: formatMessage({ defaultMessage: 'Spenderat' }),
      bgColor: 'bg-spent',
      color: 'text-spentContrast',
      value: portalSpent,
      percentage: portalPercentage,
    },
  ];

  if (!hasNoCreditLimit) {
    barItems.push({
      label: formatMessage({ defaultMessage: 'Outnyttjat' }),
      bgColor: 'bg-background-300',
      color: 'text-text-700',
      value: remainingCredit,
      percentage: remainingPercentage,
    });
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {includeTitle && (
        <h2 className="w-full">
          <FormattedMessage defaultMessage="Kredit och spendering" />
        </h2>
      )}

      <p className="text-text-800">
        <FormattedMessage defaultMessage="Organisationens information för nuvarande kalendermånad." />{' '}
        {hasSchoolDepartmentAccess && !studentsDisabled && (
          <FormattedMessage defaultMessage="Inkluderar inte skolbiljetter och kort." />
        )}
      </p>

      <p className="text-text-800">
        {hasNoCreditLimit ? (
          <FormattedMessage defaultMessage="Organisationen har ingen kreditgräns." />
        ) : (
          <FormattedMessage
            defaultMessage="Organisationens kreditgräns: {orgCredit} SEK"
            values={{ orgCredit: organization.credit?.toLocaleString(locale) }}
          />
        )}
      </p>

      <CreditInfoBar items={barItems} hideBar={totalSpent === 0} />
    </div>
  );
};
